<!-- TeamTransferDialog.vue -->
<template>
  <BaseDialog
    v-model="internalValue"
    :title="$t('team_transfer_dialog.title')"
    :subtitle="$t('team_transfer_dialog.subtitle')"
    :confirm-text="$t('team_transfer_dialog.transfer_player')"
    :cancel-text="$t('return')"
    :loading="loading"
    @cancel="onCancel"
    @confirm="onConfirm"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="'transfer-dialog-title'"
    :aria-describedby="'transfer-dialog-description'"
  >
    <!-- ───── Context Up-Front ───── -->
    <div class="player-context mb-2 ml-1" role="group" aria-labelledby="player-info-label">
      <div class="subtitle-1 font-weight-medium" id="player-info-label">
        {{ personData.name }} {{ personData.surname }}
      </div>
      <div class="caption text--secondary">
        {{ personAge }} {{$t('years')}} · {{ currentTeamName }} ({{ currentCategory }})
      </div>
    </div>

    <!-- ───── Team Picker ───── -->
    <v-select
      v-model="selectedTeam"
      :items="groupedTeams"
      item-text="name"
      item-value="id"
      :label="$t('team_transfer_dialog.select_new_team')"
      solo dense
      :error="!!selectError"
      :error-messages="selectError"
      aria-label="Select new team for player transfer"
      :aria-describedby="'team-select-description'"
    >
      <template v-slot:item="data">
        <v-list-tile-content>
          <v-list-tile-title :style="{ fontSize: teamsFontSize, marginLeft: '10px' }">
            {{ getBaseName(data.item.name) }}
            <strong :style="{ fontSize: teamsFontSize }" v-if="getSuffix(data.item.name)">
              {{ getSuffix(data.item.name) }}
            </strong>
          </v-list-tile-title>
        </v-list-tile-content>
      </template>
      <template v-slot:selection="data">
        <span :style="{ fontSize: teamsFontSize }">
          {{ getCategoryName(data.item) }}
          {{ getBaseName(data.item.name) }}
          <strong :style="{ fontSize: teamsFontSize }" v-if="getSuffix(data.item.name)">
            {{ getSuffix(data.item.name) }}
          </strong>
        </span>
      </template>
    </v-select>

    <p id="team-select-description" class="mt-2" style="font-size: 14px; color: #555;">
      {{ $t('team_transfer_dialog.subtext') }}
    </p>
  </BaseDialog>
</template>

<script>
/* global moment */
import BaseDialog from '@/components/base/BaseDialog.vue'

export default {
  name: 'TeamTransferDialog',
  components: {
    BaseDialog
  },
  props: {
    value: { type: Boolean, required: true },
    team: {
      type: Object,
      required: false,
      default: () => ({})
    },
    title: {
      type: String,
      default: 'Transfer Player'
    },
    categories: {
      type: Object,
      required: false,
      default: () => ({})
    },
    persons: { type: Array, required: true },
    playersException: {
      type: [Array, Boolean],
      default: false
    },
    personId: {
      type: [String],
      required: false
    },
    transferError: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      selectedTeam: null,
      teamsFontSize: '16px',
      selectError: '',
      errorBirthday: false,
      errorCategoryYear: false,
      errorExceptionLimit: false
    }
  },
  computed: {
    personData () {
      return this.persons.find(p => p.id === this.personId) || {}
    },
    personAge () {
      return this.personData.birthday
        ? moment().diff(this.personData.birthday, 'years')
        : ''
    },
    personInitials () {
      const { name = '', surname = '' } = this.personData
      return (name.charAt(0) + surname.charAt(0)).toUpperCase()
    },
    currentTeamName () {
      return (this.team && this.team.team && this.team.team.name) ? this.team.team.name : ''
    },
    currentCategory () {
      return (this.team && this.team.team && this.team.team.category && this.team.team.category.max_age) ? this.team.team.category.max_age : ''
    },
    internalValue: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },

    groupedTeams () {
      const items = []
      const currentTeamId = this.team && this.team.team && this.team.team ? this.team.team.id : null
      const categories = this.categories || {}

      Object.values(categories).forEach(groupObj => {
        const teams = Object.values(groupObj).filter(t => t.id !== currentTeamId)
        if (!teams.length) return // Skip empty groups

        const { max_age: maxAge } = teams[0].category
        const catUpper = this.$i18n.t('params.category').toUpperCase()
        items.push({
          header: `${catUpper} ${maxAge}`,
          disabled: true
        })

        teams.forEach(t => {
          items.push({
            name: t.name,
            id: t.id
          })
        })
      })

      return items
    }
  },
  methods: {
    onCancel () {
      if (this.loading) return
      this.$emit('input', false) // close the dialog
      this.selectedTeam = null // reset local state
      this.selectError = ''
    },
    onConfirm () {
      this.selectError = ''

      if (!this.selectedTeam) {
        this.selectError = this.$t('team_transfer_dialog.select_new_team')
        return
      }
      this.$emit('confirm', this.selectedTeam)
    },
    getSuffix (name) {
      // Extract the last uppercase letter from the name
      if (!name) return ''
      const match = name.match(/\b([A-Z])$/)
      return match ? match[1] : ''
    },
    getBaseName (name) {
      // Remove the last uppercase letter and trim the string
      if (!name) return ''
      return name.replace(/\b([A-Z])$/, '').trim()
    },
    getCategoryName (item) {
      if (!item) return ''
      const teamId = item.id
      let team
      for (const teams of Object.values(this.categories)) {
        if (teams[teamId]) {
          team = teams[teamId]
          break
        }
      }
      if (!team || !team.category) return ''
      return team.category.max_age + ' - '
    },
    getPerson (personId) {
      if (!personId) return null
      return this.persons.find(p => p.id === personId)
    },
    clearErrors () {
      this.errorBirthday = false
      this.errorCategoryYear = false
      this.errorExceptionLimit = false
      this.selectError = ''
    }
  },
  watch: {
    selectedTeam (newTeamId) {
      this.clearErrors()

      const team = Object.values(this.categories)
        .flatMap(Object.values)
        .find(t => t.id === newTeamId)

      if (!team) {
        this.selectError = 'Team not found.'
        return false
      }

      const category = team.category || {}
      const categoryYear = Number(category.year)
      const person = this.getPerson(this.personId)

      if (!person) {
        this.selectError = 'Player not found.'
        return false
      }

      const birthYear = moment(person.birthday, 'YYYY-MM-DD').year()
      const playerAgeWarning = this.$t('team_transfer_dialog.player_too_old_params', { birthYear, categoryLabel: category.max_age })

      // too young?
      if (birthYear < categoryYear) {
        this.errorBirthday = true
        this.errorCategoryYear = true

        // exception-year case?
        if (categoryYear - birthYear === 1) {
          const birthMonth = moment(person.birthday, 'YYYY-MM-DD').month()
          if (birthMonth >= 0) {
          // fetch current exceptions for this team:
            const exceptions = this.playersException || []
            if (exceptions.length < category.num_players_exception) {
              this.clearErrors()
              return true
            }

            // allow if already in exceptions
            if (exceptions.includes(person.id)) {
              this.clearErrors()
              return true
            }

            this.errorExceptionLimit = true
            this.selectError = playerAgeWarning
            return false
          } else {
            this.selectError = playerAgeWarning
            return false
          }
        }
        this.selectError = playerAgeWarning
        return false
      }

      // valid
      this.clearErrors()
      return true
    },
    transferError (newMsg) {
      this.selectError = this.$i18n.t(newMsg)
    }
  }
}
</script>
